import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { BRAND } from "@/config/brand";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  Wrench, Compass, Sparkles, ShieldCheck, ArrowRight, GraduationCap,
  FileText, Folder, ListChecks, Briefcase,
} from "lucide-react";

function PlaceholderCard({ icon: Icon, title, body, testid, actionLabel, actionTo, status }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-border-subtle bg-bg-surface p-6 flex flex-col"
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-accent-warm/10 text-accent-warm">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <h2 className="text-lg font-heading font-medium text-fg-primary">{title}</h2>
      </div>
      <p className="text-sm leading-relaxed text-fg-secondary flex-1">{body}</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {status && (
          <span
            data-testid={`${testid}-status`}
            className="inline-flex text-xs uppercase tracking-wider text-fg-secondary bg-bg-surface-hover px-2 py-1 rounded-sm"
          >
            {status}
          </span>
        )}
        {actionLabel && actionTo && (
          <Link to={actionTo}>
            <Button
              size="sm"
              data-testid={`${testid}-action`}
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              {actionLabel}
              <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const greetingName = user?.display_name || (user?.email ? user.email.split("@")[0] : "there");
  const [resume, setResume] = useState(null);
  const [laneInfo, setLaneInfo] = useState(null);
  const [quickSnapshotState, setQuickSnapshotState] = useState(null);
  const [resumeStudio, setResumeStudio] = useState(null);
  const [access, setAccess] = useState(null);

  useEffect(() => {
    api.get("/journey/resume").then((r) => setResume(r.data.resume)).catch(() => {});
    // Fetch current lane state so the assessment CTA can route to the
    // lane-choice screen for first-time users and preserve their lane
    // otherwise. This is a soft signal — failures are silent.
    api.get("/assessment/lanes").then((r) => setLaneInfo(r.data)).catch(() => {});
    // Quick Repath snapshot discoverability — the card below can then show
    // the right label based on whether a snapshot exists yet.
    api.get("/assessment/quick-snapshot/current").then((r) => setQuickSnapshotState(r.data)).catch(() => setQuickSnapshotState(null));
    // Resume Studio entry-card visibility: only surfaced once the user
    // has selected at least one CRA3D career direction, so the card
    // appears only when they've reached a meaningful state.
    api.get("/resume-studio/status").then((r) => setResumeStudio(r.data)).catch(() => setResumeStudio(null));
    // Access resolution — drives the substantial Upgrade card visibility
    // so paid users never see a duplicate-purchase prompt.
    api.get("/me/access").then((r) => setAccess(r.data)).catch(() => setAccess(null));
  }, []);

  const hasCompleteAccess = !!access?.capabilities?.complete_assessment_access;

  // H2 (R29): AI Training ownership — accurate labels before AND after
  // purchase. Prevents the "I have active access" misreading that
  // conflated the FREE Explorer plan's assessment_access with paid
  // training access. Driven by `active_grants` from /api/me/access,
  // which is the single source of truth the webhook writes to.
  const trainingGrants = (access?.active_grants || []).filter(
    (g) => typeof g.product_slug === "string" && g.product_slug.startsWith("training_"),
  );
  const hasTrainingGrant = trainingGrants.length > 0;
  const trainingPackageName = hasTrainingGrant
    ? (trainingGrants[0]?.product?.name || trainingGrants[0]?.product_slug)
    : null;

  const currentLane = laneInfo?.current_lane;
  const assessmentCta = (() => {
    // If no lane chosen yet AND no answers on either lane, route to the
    // lane-choice screen. Otherwise keep them in their current lane.
    if (!laneInfo) return { label: "Open assessment", to: "/assessment" };
    const anyAnswers = (laneInfo.quick?.answered_carryover || 0) + (laneInfo.complete?.answered_carryover || 0);
    if (!currentLane && anyAnswers === 0) {
      return { label: "Choose how to begin", to: "/assessment/choose" };
    }
    return { label: currentLane === "quick" ? "Continue Quick Repath" : "Continue Complete Career Repath Assessment", to: `/assessment?lane=${currentLane || "complete"}` };
  })();

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-8">
          <p className="text-sm text-fg-secondary">Welcome to {BRAND.name}</p>
          <h1
            data-testid="dashboard-greeting"
            className="mt-1 text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary"
          >
            Hi {greetingName}.
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-fg-secondary">
            This is your working space. Everything you produce here is a draft you own —
            no fake achievements, no urgency, no locked doors.
          </p>
        </div>

        {resume && resume.stage === "assessment_not_started" && !laneInfo?.current_lane ? (
          <div
            className="mb-8 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-5 flex flex-wrap items-center justify-between gap-4"
            data-testid="dashboard-start-here-banner"
          >
            <div>
              <p className="text-xs uppercase tracking-wider text-accent-warm">Start here</p>
              <p
                data-testid="dashboard-start-here-title"
                className="mt-1 text-lg font-heading font-medium text-fg-primary"
              >
                Start here &mdash; choose your assessment
              </p>
              <p
                data-testid="dashboard-start-here-supporting"
                className="mt-1 text-sm text-fg-secondary max-w-xl"
              >
                Start with the free 18-question Quick Repath and preliminary
                snapshot, or choose the full 56-question assessment for $24.99
                one-time.
              </p>
            </div>
            <Link to="/assessment/choose">
              <Button
                data-testid="dashboard-start-here-btn"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                Choose how to begin &rarr;
              </Button>
            </Link>
          </div>
        ) : resume && (
          <div className="mb-8 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="continue-banner">
            <div>
              <p className="text-xs uppercase tracking-wider text-accent-warm">Continue where you left off</p>
              <p data-testid="continue-title" className="mt-1 text-lg font-heading font-medium text-fg-primary">{resume.title}</p>
              <p data-testid="continue-why" className="mt-1 text-xs text-fg-secondary">{resume.why}</p>
            </div>
            <Link to={resume.route}>
              <Button data-testid="continue-btn" className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
                {resume.stage === "finished" ? "Open your portfolio" : "Continue"}
                <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
              </Button>
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-cards">
          {/* Upgrade card — substantial CTA for unpaid users. Hidden
              once the user holds the Complete grant so no duplicate-
              purchase prompt is ever shown. */}
          {access && !hasCompleteAccess && (
            <div
              data-testid="dashboard-upgrade-card"
              className="md:col-span-2 lg:col-span-3 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
            >
              <div>
                <p className="text-xs uppercase tracking-wider text-accent-warm">
                  Ready for stronger evidence?
                </p>
                <h2
                  data-testid="dashboard-upgrade-card-title"
                  className="mt-1 text-xl font-heading font-medium text-fg-primary"
                >
                  Unlock the Complete Career Repath Assessment
                </h2>
                <p
                  data-testid="dashboard-upgrade-card-body"
                  className="mt-2 text-sm text-fg-secondary max-w-2xl leading-relaxed"
                >
                  56 questions, three evidence-linked lanes of direction, the
                  full PDF report, JSON export of your answers and analysis,
                  and an initial action-plan outline &mdash; a single{" "}
                  <span className="text-fg-primary font-medium">$24.99</span>{" "}
                  one-time payment. Your existing answers carry forward
                  automatically.
                </p>
              </div>
              <div className="flex-shrink-0">
                <Link to="/plans?intent_slug=three_directions">
                  <Button
                    data-testid="dashboard-upgrade-card-cta"
                    className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                  >
                    Upgrade &mdash; $24.99
                    <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
          <PlaceholderCard
            testid="dashboard-card-desire"
            icon={Compass}
            title="Career Repath Assessment"
            body={
              currentLane === "quick"
                ? "You're on the Quick Repath. 18 questions total. You can continue into the Complete Career Repath Assessment whenever you're ready — matching answers carry over."
                : currentLane === "complete"
                ? "You're on the Complete Career Repath Assessment. 56 questions total. Save and resume any time."
                : "Two ways to begin: a short Quick Repath (18 questions, preliminary snapshot) or the Complete Career Repath Assessment (56 questions, full profile). Neither guarantees employment or income."
            }
            actionLabel={assessmentCta.label}
            actionTo={assessmentCta.to}
            status={currentLane === "quick" ? "Quick Repath" : currentLane === "complete" ? "Complete Career Repath Assessment" : null}
          />
          {currentLane === "quick" && (laneInfo?.quick?.answered_carryover || 0) >= 18 && (
            <PlaceholderCard
              testid="dashboard-card-quick-snapshot"
              icon={Sparkles}
              title="Your Quick Repath Snapshot"
              body={
                quickSnapshotState?.snapshot
                  ? "A preliminary read from your Quick Repath answers. Revisitable, correctable, and it stays even if you continue to the Complete Career Repath Assessment."
                  : "You've finished the 18 Quick Repath questions. Generate a preliminary read whenever you're ready — nothing is locked in."
              }
              actionLabel={quickSnapshotState?.snapshot ? "Open your snapshot" : "Open the snapshot page"}
              actionTo="/assessment/quick/snapshot"
              status={quickSnapshotState?.snapshot ? "Ready" : "Ready to generate"}
            />
          )}
          <PlaceholderCard
            testid="dashboard-card-briefing"
            icon={Compass}
            title="Career Repath Assessment briefing"
            body="A calm summary of what CRA3D heard, 3 peer Lanes of Direction, and skill-gap previews. Revisit any time."
            actionLabel="Open briefing"
            actionTo="/briefing"
          />
          <PlaceholderCard
            testid="dashboard-card-results"
            icon={Sparkles}
            title="Your Results workspace"
            body="Submit the assessment to open your evidence-linked profile and 3 participant-controlled Lanes of Direction."
            actionLabel="Open results"
            actionTo="/results"
          />
          <PlaceholderCard
            testid="dashboard-card-portfolio"
            icon={GraduationCap}
            title="AI Foundations & Practical Skills"
            body="Nine short modules plus three practical pathways. Templates, checklists, and real exercises."
            actionLabel="Open learning"
            actionTo="/learning"
          />
          {/* H2 (R29): AI Training ownership label — accurate for users
              WITHOUT a training grant (shows "Not purchased" + routes to
              /plans) and for users WITH a grant (shows the actual package
              name + routes to /training). Prevents the Free Explorer
              assessment access from being misread as paid training. */}
          {hasTrainingGrant ? (
            <PlaceholderCard
              testid="dashboard-card-training-owned"
              icon={GraduationCap}
              title="AI Training"
              body={
                `You own the ${trainingPackageName || "AI Training package"}. Open any unlocked month to resume your lessons; your progress is preserved.`
              }
              actionLabel="Open AI Training"
              actionTo="/training"
              status="Active"
            />
          ) : (
            <PlaceholderCard
              testid="dashboard-card-training-not-purchased"
              icon={GraduationCap}
              title="AI Training"
              body="Practical, skill-building AI lessons across up to twelve months. You haven't purchased a training package yet — browse the available packages when you're ready. Nothing is locked in."
              actionLabel="Browse training packages"
              actionTo="/plans"
              status="Not purchased"
            />
          )}
          <PlaceholderCard
            testid="dashboard-card-portfolio-view"
            icon={Folder}
            title="Your portfolio"
            body="Review your completed capstones, revise them (revision history is preserved), and choose which to show."
            actionLabel="Open portfolio"
            actionTo="/portfolio"
          />
          <PlaceholderCard
            testid="dashboard-card-action-plan"
            icon={ListChecks}
            title="Your Action Planner"
            body="Organize your next 30, 60 and 90 days. Edit and save your own steps."
            actionLabel="Open planner"
            actionTo="/action-plan"
          />
          <PlaceholderCard
            testid="dashboard-card-opportunity"
            icon={Briefcase}
            title="CRA3D Job Launch — Coming soon"
            body="Job-launch tools are in development. Preview what's planned; nothing is live yet."
            actionLabel="Preview"
            actionTo="/opportunity"
          />
          {resumeStudio?.has_selected_direction && (
            <PlaceholderCard
              testid="dashboard-card-resume-studio"
              icon={FileText}
              title="Resume Studio"
              body={
                resumeStudio.saved_count > 0
                  ? `You have ${resumeStudio.saved_count} resume${resumeStudio.saved_count === 1 ? "" : "s"} in progress. Build and preview free — downloads require a purchase later.`
                  : "Build a truthful, editable resume from what you already told CRA3D. Building and previewing is free; downloads will require a purchase later."
              }
              actionLabel={resumeStudio.saved_count > 0 ? "Open Resume Studio" : "Start a resume"}
              actionTo="/resume-studio"
              status="Building & preview free"
            />
          )}
        </div>

        <div className="mt-10 rounded-lg border border-border-subtle bg-bg-surface p-6" data-testid="dashboard-privacy-block">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-progress-teal/10 text-progress-teal">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-lg font-heading font-medium text-fg-primary">You own your data</h2>
              <p className="mt-2 text-sm leading-relaxed text-fg-secondary">
                You can{" "}
                <Link to="/settings" data-testid="dashboard-settings-link" className="text-accent-warm underline underline-offset-2 decoration-accent-warm/40 hover:decoration-accent-warm">
                  export or delete your account
                </Link>{" "}
                at any time from Settings. Please do not enter confidential
                information belonging to any employer, client, or third party.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
